import React, { useState } from "react";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import FormHelperText from "@mui/material/FormHelperText";
import { OutlinedInputProps } from "@mui/material/OutlinedInput";
import {
  Typography,
  useMediaQuery,
  useTheme,
  Box,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { FieldError, UseFormRegister } from "react-hook-form";
import "./AuthInput.css";

export type CustomInputProps = {
  name: string;
  register: UseFormRegister<any>;
  rules?: any;
  helperText?: string;
  error?: boolean | FieldError;
  type?: string;
  endAdornment?: React.ReactNode;
};

interface ExtendedCustomInputProps
  extends Omit<CustomInputProps, "placeholder"> {
  InputProps?: Partial<OutlinedInputProps>;
  label?: string;
  isRequired?: boolean;
  labelWidth?: string;
  labelWidthSp?: string;
  inputWidth?: string;
  inputWidthSp?: string;
  labelSx?: any;
  placeholder?: string;
  apiError?: string;
  successMessage?: string;
  showPasswordToggle?: boolean;
}

const AuthInput: React.FC<ExtendedCustomInputProps> = ({
  helperText,
  error,
  type = "text",
  register,
  name,
  rules,
  endAdornment,
  InputProps = {},
  label,
  isRequired = false,
  labelWidth = "100%",
  labelWidthSp = "100%",
  inputWidth = "100%",
  inputWidthSp = "100%",
  labelSx = {},
  apiError,
  successMessage,
  showPasswordToggle = false,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleFocus = () => setIsFocused(true);
  const handleBlur = () => setIsFocused(false);
  const handleTogglePasswordVisibility = () => setShowPassword(!showPassword);

  const getValidationRules = () => {
    const validationRules: any = {};
    if (isRequired) {
      validationRules.required = `${label || name}は必須です`;
    }
    if (type === "email") {
      validationRules.pattern = {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "有効なメールアドレスを入力してください",
      };
    }
    if (type === "phoneNumber") {
      validationRules.pattern = {
        value: /^\d{10,11}$/,
        message: "Phone番号は10桁または11桁の数字である必要があります",
      };
    }
    return { ...validationRules, ...rules };
  };

  const getDisplayMessage = () => {
    if (successMessage) return successMessage;
    if (typeof helperText === "string" && helperText) return helperText;
    if (apiError) return apiError;
    return undefined;
  };

  const hasError = !!error || !!apiError;

  const getPasswordToggleAdornment = () => {
    if (!showPasswordToggle || type !== "password") {
      return endAdornment || InputProps.endAdornment;
    }
    return (
      <InputAdornment position="end">
        <IconButton
          aria-label="toggle password visibility"
          onClick={handleTogglePasswordVisibility}
          onMouseDown={(e) => e.preventDefault()}
          edge="end"
        >
          {showPassword ? (
            <VisibilityOff sx={{ opacity: 0.6 }} />
          ) : (
            <Visibility sx={{ opacity: 0.6 }} />
          )}
        </IconButton>
      </InputAdornment>
    );
  };

  const getInputType = () => {
    if (showPasswordToggle && type === "password") {
      return showPassword ? "text" : "password";
    }
    return type;
  };

  return (
    <Box className={`inputGroupWrapper flexCol ${isMobile ? "sp" : ""}`}>
      {label && (
        <Typography
          className={`inputLabel ${isMobile ? "sp" : ""}`}
          sx={{
            width: { lg: labelWidth, xs: labelWidthSp },
            ...labelSx,
            fontSize: "12px !important",
            fontWeight: "500 !important",
            alignSelf: "flex-start",
            paddingTop: "0",
            paddingBottom: "4px",
            marginBottom: "1px",
            textAlign: "left",
            justifyContent: "flex-start",
          }}
        >
          {label}
        </Typography>
      )}
      <Box sx={{ width: { sm: inputWidth, xs: inputWidthSp } }}>
        <FormControl
          className={`formControl ${isMobile ? "sp" : ""} ${
            hasError ? "formControlError" : ""
          }`}
          fullWidth
        >
          <OutlinedInput
            type={getInputType()}
            {...register(name, getValidationRules())}
            onFocus={handleFocus}
            onBlur={handleBlur}
            endAdornment={getPasswordToggleAdornment()}
            error={hasError}
            {...InputProps}
          />
          {getDisplayMessage() && !hasError && !successMessage && (
            <FormHelperText
              className={`formControlHelperText formControlHelperTextNormal ${
                isFocused ? "focus" : ""
              }`}
            >
              {getDisplayMessage()}
            </FormHelperText>
          )}
          {getDisplayMessage() && hasError && (
            <FormHelperText className="formControlHelperText formControlHelperTextError">
              {getDisplayMessage()}
            </FormHelperText>
          )}
          {successMessage && (
            <FormHelperText className="formControlHelperText formControlHelperTextSuccess">
              {successMessage}
            </FormHelperText>
          )}
        </FormControl>
      </Box>
    </Box>
  );
};

export default AuthInput;
